import React, { useState } from 'react'
import { Translate } from 'react-localize-redux'
import {
  Box,
  Typography,
  IconButton,
  CardActionArea,
  Divider,
  Tooltip,
  Badge,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import ShareIcon from '@material-ui/icons/Share'
import FavoriteIcon from '@material-ui/icons/Favorite'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AddCircleRoundedIcon from '@material-ui/icons/AddCircleRounded'
import EditIcon from '@material-ui/icons/Edit'
import { SubLink } from 'containers'
import LockIcon from '@material-ui/icons/Lock'
import useTeamFeature from 'hooks/useTeamFeature'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import fallbackImageUrl from '../../assets/images/login-coach.jpg'
import {
  MuiCard,
  MuiBox,
  CardMedia,
  CardContent,
  CardActions,
} from './components/StyledComponents'
import DrillViewDialog from './components/DrillViewDialog'
import PhaseMenu from './components/PhaseMenu'
import AddDrillToTrainingDialog from './components/AddDrillToTrainingDialog'
import { useStyles } from './styles'
import PremiumContentDialog from '../PremiumContentDialog'
import { DRILL_EDIT_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const DrillCard = ({
  drill,
  withPhaseMenu,
  onFavoriteToggle,
  onDuplicate,
  language,
  showAddToTrainingButton,
  onPhaseSelect,
  showDrillActions,
}) => {
  const classes = useStyles()
  const [drillViewDialogOpen, setDrillViewDialogOpen] = useState(false)
  const [addToTrainingModalOpen, setAddToTrainingModalOpen] = useState(false)
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false)

  const featureGuard = useFeatureAccessGuard()

  const canUpdate = drill.can.includes('update')

  const { enabled: hasActivitiesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.activities_enabled
  )
  const { enabled: hasAdvancedTrainingFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_training_features_enabled
  )
  const { enabled: hasCreateActivitiesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.create_activities
  )

  const toggleDrillViewDialogOpen = () => {
    if (featureGuard(hasActivitiesEnabled)) return

    setDrillViewDialogOpen(!drillViewDialogOpen)
  }

  const handlePhaseSelect = (phase) => {
    onPhaseSelect(drill, phase)
  }

  const toggleAddToTrainingDialog = () => {
    if (featureGuard(hasAdvancedTrainingFeaturesEnabled)) return

    setAddToTrainingModalOpen(!addToTrainingModalOpen)
  }

  const imageUrl =
    drill.relationships.images.length > 0
      ? drill.relationships.images[drill.relationships.images.length - 1]
          .full_url
      : fallbackImageUrl

  const handleShareClick = () => {
    if (featureGuard(hasActivitiesEnabled)) return

    const shareUrl = `${process.env.REACT_APP_API_URL}/drills/${drill.uuid}/share?language=${language}`
    window.open(shareUrl, '_blank')
  }

  const handleCardClick = () => {
    if (
      !hasActivitiesEnabled ||
      (drill.locked && drill.locked_ff === 'cb_premium_content')
    ) {
      featureGuard()
    } else if (drill.locked && drill.locked_ff !== 'cb_premium_content') {
      setPremiumDialogOpen(true)
    } else {
      toggleDrillViewDialogOpen()
    }
  }

  const handleClosePremiumDialog = () => {
    setPremiumDialogOpen(false)
  }

  return (
    <>
      {drillViewDialogOpen && (
        <DrillViewDialog
          drill={drill}
          hanldeOpenClose={toggleDrillViewDialogOpen}
          open={drillViewDialogOpen}
          withPhaseMenu={withPhaseMenu}
          handlePhaseSelect={handlePhaseSelect}
        />
      )}
      {addToTrainingModalOpen && (
        <AddDrillToTrainingDialog
          open={addToTrainingModalOpen}
          handleClose={toggleAddToTrainingDialog}
          drill={drill}
        />
      )}
      {premiumDialogOpen && (
        <PremiumContentDialog
          open={premiumDialogOpen}
          onClose={handleClosePremiumDialog}
          lockedFeatureFlag={drill.locked_ff}
        />
      )}
      <MuiCard>
        <CardActionArea onClick={handleCardClick}>
          <CardMedia image={imageUrl} />
          <CardContent>
            <Typography gutterBottom variant="h4" component="h4" noWrap>
              {drill.title}{' '}
              {!hasActivitiesEnabled || drill.locked ? (
                <LockIconComponent
                  context="secondaryButton"
                  style={{ float: 'right' }}
                />
              ) : null}
            </Typography>
            <MuiBox>
              <Box>
                <Typography>
                  <Translate id="drills.duration" />:
                </Typography>
                <Typography>
                  {typeof drill.duration === 'number' &&
                    drill.duration !== null &&
                    `${drill.duration} min`}
                </Typography>
              </Box>
              <Box>
                <Typography>
                  <Translate id="drills.players" />:
                </Typography>
                <Typography>
                  {`${drill.min_players}P - ${drill.max_players}P`}
                </Typography>
              </Box>
            </MuiBox>
          </CardContent>
        </CardActionArea>
        <Divider />
        <CardActions>
          {!drill.locked && (
            <>
              <Box>
                {withPhaseMenu && (
                  <PhaseMenu onPhaseSelect={handlePhaseSelect} />
                )}
                {showAddToTrainingButton && (
                  <Tooltip title={<Translate id="training.add-to-training" />}>
                    <IconButton
                      onClick={toggleAddToTrainingDialog}
                      size="small"
                      color="primary"
                    >
                      <Badge
                        className={classes.lockBadgeOnIconButton}
                        badgeContent={
                          !hasAdvancedTrainingFeaturesEnabled ? (
                            <LockIcon color="primary" fontSize="small" />
                          ) : null
                        }
                        overlap="circular"
                      >
                        <AddCircleRoundedIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {showDrillActions && (
                <>
                  {canUpdate && (
                    <Tooltip title={<Translate id="button.edit" />}>
                      <IconButton
                        size="small"
                        className={classes.drillIcon}
                        {...(!hasCreateActivitiesEnabled
                          ? {
                              onClick: () => featureGuard(),
                            }
                          : {
                              component: SubLink,
                              to: DRILL_EDIT_ROUTE.populateURLParams({
                                drillUuid: drill.uuid,
                              }),
                            })}
                      >
                        <Badge
                          className={classes.lockBadgeOnIconButton}
                          badgeContent={
                            !hasCreateActivitiesEnabled ? (
                              <LockIcon color="primary" fontSize="small" />
                            ) : null
                          }
                          overlap="circular"
                        >
                          <EditIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={<Translate id="button.duplicate" />}>
                    <IconButton onClick={onDuplicate} size="small">
                      <Badge
                        className={classes.lockBadgeOnIconButton}
                        badgeContent={
                          !hasCreateActivitiesEnabled ? (
                            <LockIcon color="primary" fontSize="small" />
                          ) : null
                        }
                        overlap="circular"
                      >
                        <FileCopyIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={<Translate id="button.share" />}>
                    <IconButton onClick={handleShareClick} size="small">
                      <Badge
                        className={classes.lockBadgeOnIconButton}
                        badgeContent={
                          !hasActivitiesEnabled ? (
                            <LockIcon color="primary" fontSize="small" />
                          ) : null
                        }
                        overlap="circular"
                      >
                        <ShareIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </>
          )}
          <Tooltip title={<Translate id="drills.favorite" />}>
            <IconButton onClick={onFavoriteToggle} size="small">
              <FavoriteIcon
                color={drill.favorite ? 'primary' : 'inherit'}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </CardActions>
      </MuiCard>
    </>
  )
}

DrillCard.defaultProps = {
  drill: {
    min_players: 0,
    relationships: {
      images: [],
    },
  },
}

DrillCard.defaultProps = {
  withPhaseMenu: false,
  onPhaseSelect: () => {},
  showDrillActions: true,
}

DrillCard.propTypes = {
  drill: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    relationships: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          full_url: PropTypes.string.isRequired,
        })
      ),
    }),
    duration: PropTypes.number,
    min_players: PropTypes.number,
    max_players: PropTypes.number.isRequired,
    favorite: PropTypes.bool.isRequired,
    can: PropTypes.arrayOf(PropTypes.string),
    locked: PropTypes.bool.isRequired,
    locked_ff: PropTypes.string,
  }),
  withPhaseMenu: PropTypes.bool,
  onFavoriteToggle: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  showAddToTrainingButton: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  onPhaseSelect: PropTypes.func,
  showDrillActions: PropTypes.bool,
}

export default DrillCard
